import { useMemo, useSyncExternalStore } from 'react';
import { useIsMounted } from '@toss/react';

import { getExternalPlatformDebugOverride } from '../../externalPlatform/debug';
import { googleClassroomContextManager } from '../GoogleClassroomContextManager';
import { UserType } from '../types';

export function useIsInGoogleClassroom() {
  const isMounted = useIsMounted();
  // 컨텍스트가 sessionStorage 에 늦게(bridge effect) 써져도 재평가되도록 구독한다.
  // 이게 없으면 memo 가 최초 렌더의 stale-false 로 고정돼, GC 진입 유저에게 CRM 등
  // 게이트가 뒤늦게도 열리지 않음을 보장할 수 없다.
  const contextSnapshot = useSyncExternalStore(
    googleClassroomContextManager.subscribe,
    googleClassroomContextManager.getContextSnapshot,
    () => null,
  );

  return useMemo(() => {
    if (!isMounted) {
      return false;
    }

    const debugOverride = getExternalPlatformDebugOverride();
    if (debugOverride) return debugOverride.mode === 'googleClassroom';

    // 지속된 컨텍스트(sessionStorage) 우선, 없으면 진입 URL 로 즉시 판별한다.
    // 후자는 bridge 가 persist 하기 전 '첫 프레임'에도 GC 를 감지해, CRM identify/
    // campaigns 가 한 프레임도 새지 않게 게이트를 닫는다(외부플랫폼 정책).
    const context =
      googleClassroomContextManager.getLandingPageContext() ??
      googleClassroomContextManager.getLandingPageContextFromCurrentUrl();
    if (context == null) {
      return false;
    }

    switch (context.userType) {
      case UserType.Student:
        return true;
      case UserType.Teacher:
        return true;
      case UserType.Unknown:
        return false;
    }
    // contextSnapshot 은 body 에서 직접 참조하지 않지만, sessionStorage 컨텍스트가
    // 바뀌면 재평가를 트리거하기 위한 의도된 의존성이다(body 는 manager 를 통해 그 값을 읽음).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, contextSnapshot]);
}
